import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  createStorageObjectVersion,
  deleteStorageObjectVersion,
  getAllStorageObjectVersions,
  updateStorageObjectVersion,
} from "../../../../services/api/instances/storageApi/versioningMethods";
import type { RootState } from "../../../../services/redux/types";
import { makeSelectServiceField } from "../selectors";
import { setObjectVersions, setSelectedVersionId } from "../slice";
import type { StorageServiceVersions } from "../types";
import { removeVersionAndRefresh } from "../utils/removeVersionAndRefresh";
import {
  StorageObjectValue,
  StorageScope,
} from "../../../../services/api/instances/storageApi/types/service_types";
import { StorageObjectVersion } from "../../../../services/api/instances/storageApi/types/object_types";
import { getError } from "../../../../services/lib/getError";

export const fetchAllStorageObjectVersions = createAsyncThunk(
  "storage/fetchAllStorageVersions",
  async ({ service }: { service: StorageScope }, ThunkAPI) => {
    try {
      const state = ThunkAPI.getState() as RootState;
      const id = state.storageObjectState[service].selectedId as string;
      const versions = state.storageObjectState[service]
        .versions as StorageServiceVersions;
      const objectVersions = await getAllStorageObjectVersions(
        id as string,
        service,
      );
      return { ...(versions as StorageServiceVersions), [id]: objectVersions };
    } catch (error: unknown) {
      const message = getError(error);
      return ThunkAPI.rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const createStorageObjectVersionAction = createAsyncThunk(
  "storage/createStorageObjectVersion",
  async (
    { service, name }: { service: StorageScope; name: string },
    ThunkAPI,
  ) => {
    try {
      const state = ThunkAPI.getState() as RootState;
      const id = state.storageObjectState[service].selectedId as string;
      const versions = state.storageObjectState[service]
        .versions as StorageServiceVersions;
      const date = new Date();
      const postObj = {
        version_name: `${name}`,
        version_date: date.toISOString(),
      };
      const newVersion = await createStorageObjectVersion(
        id as string,
        postObj,
        service,
      );
      const objectVersions = versions[id] ? [...versions[id]] : [];
      ThunkAPI.dispatch(
        setSelectedVersionId({
          service,
          version_id: newVersion?.version_id as string,
        }),
      );
      return {
        ...(versions as StorageServiceVersions),
        [id]: [...objectVersions, newVersion],
      };
    } catch (error: unknown) {
      const message = getError(error);
      return ThunkAPI.rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const updateStorageObjectVersionAction = createAsyncThunk(
  "storage/updateStorageObjectVersion",
  async (
    { service, name }: { service: StorageScope; name: string },
    ThunkAPI,
  ) => {
    try {
      const state = ThunkAPI.getState() as RootState;
      const id = state.storageObjectState[service].selectedId as string;
      const versions = state.storageObjectState[service]
        .versions as StorageServiceVersions;
      const selectedVersionId = state.storageObjectState[service]
        .selectedVersionId as string;
      const date = new Date();
      const selectedVersion = versions[id].find(
        (version) => version.version_id === selectedVersionId,
      );

      if (!selectedVersion) return;

      const newVersions = {
        ...versions,
        [id]: versions[id].map((version) => {
          if (version?.version_id === selectedVersionId) {
            return {
              ...version,
              version_name: `${name}`,
              version_date: date.toISOString(),
            };
          }
          return version;
        }),
      };

      const updatedVersion: StorageObjectVersion<StorageObjectValue> = {
        ...selectedVersion,
        version_name: `${name}`,
        version_date: date.toISOString(),
      };

      await updateStorageObjectVersion(updatedVersion, service);

      return newVersions;
    } catch (error: unknown) {
      const message = getError(error);
      return ThunkAPI.rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const refreshStorageObjectVersionAction = createAsyncThunk(
  "storage/refreshStorageObjectVersion",
  async (
    { service, versionId }: { service: StorageScope; versionId: string },
    { getState, dispatch, rejectWithValue },
  ) => {
    try {
      const state = getState() as RootState;
      const id = state.storageObjectState[service].selectedId as string;
      const versions = state.storageObjectState[service]
        .versions as StorageServiceVersions;
      const selectedVersion = versions[id].find(
        (version) => version.version_id === versionId,
      );

      if (!selectedVersion) return;

      const { newVersions, objectVersions } = removeVersionAndRefresh(
        versions as StorageServiceVersions,
        id as string,
        versionId,
      );
      dispatch(setObjectVersions({ service, versions: newVersions }));
      await deleteStorageObjectVersion(id as string, versionId, service);

      await dispatch(
        createStorageObjectVersionAction({
          service,
          name: selectedVersion?.version_name as string,
        }),
      );

      return;
    } catch (error: unknown) {
      const message = getError(error);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const deleteStorageObjectVersionAction = createAsyncThunk(
  "storage/deleteStorageObjectVersion",
  async (
    { service, versionId }: { service: StorageScope; versionId: string },
    ThunkAPI,
  ) => {
    try {
      const state = ThunkAPI.getState() as RootState;
      const id = makeSelectServiceField<"selectedId">(
        service,
        "selectedId",
      )(state) as string;

      return await deleteStorageObjectVersion(id as string, versionId, service);
    } catch (error: unknown) {
      const message = getError(error);
      return ThunkAPI.rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);
